import React, { useState, useEffect, useMemo, useContext, memo } from 'react'
import { ThemeContext } from 'styled-components'
import Image from '../../../helpers/Image/Image'
import { agnostifyUrl } from '../../../helpers/utils'
import Roundel from '../../../helpers/Roundel/Roundel'
import {
  StyledHeading,
  StyledContentWrapper,
  StyledLogoWrapper,
  StyledCTA,
  StyledImageWrapper,
  StyledContainerWithLogo,
  StyledTextWrapperLogo,
  StyledContainer,
  StyledTextWrapperNoLogo
} from './C001.styles'
import { C001Props } from './types'
import { colorToLunaToken } from '../../../helpers/theming/utilities'
import { createPOIparam } from '../../../helpers/Image/utilities'
import { createRGBparam } from '../../../helpers/theming/utilities.helper'
import { createGolContentDataAttributes } from '../../../helpers/tracking'

/**
 * C001 is a Hero component that is to be used on top of a page to highlight the purpose of that page. It can be authored with an image, logoImage, background and text colour, heading and subheading text and CTA. The desktop orientation can be flipped and the image position on mobile can be changed.
 */

const C001 = ({
  heading,
  isSecondaryHeading,
  subheading,
  link = {
    url: '',
    title: '',
    target: '',
    copy: '',
    style: 'link'
  },
  image = {
    url: '',
    altText: ''
  },
  logo = {
    url: '',
    altText: ''
  },
  linkColor,
  onLinkClick,
  mobileImagePosition,
  flipOrientation = true,
  backgroundColor = 'white',
  textColor,
  textAlignment = 'left',
  tabletImagePositionTop,
  imageStyle = 'short',
  consumingGridType = 'Isolated',
  positionInGrid,
  roundel,
  citrus,
  imageFormat
}: C001Props): React.ReactElement => {
  const [isBrowse, setIsBrowse] = useState(false)

  useEffect(() => {
    setIsBrowse(typeof window !== 'undefined' && document.getElementsByClassName('browse__content-area').length > 0)
  }, [])

  const getGOLDataAttributes = (action?: string) =>
    createGolContentDataAttributes(
      consumingGridType,
      `C001 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      link?.title,
      link?.url,
      heading,
      action
    )

  const addGolTracking = (event: React.MouseEvent) => {
    const dataAttributes = getGOLDataAttributes()

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(link?.url))
    }
    addGolTracking(event)
  }

  const Container = logo?.url ? StyledContainerWithLogo : StyledContainer
  const TextWrapper = logo?.url ? StyledTextWrapperLogo : StyledTextWrapperNoLogo
  const showFullWidth = mobileImagePosition || logo?.url || !image?.url
  const fullWidthColumns = 12
  const halfWidthColumns = 6
  const xsVal = showFullWidth ? fullWidthColumns : halfWidthColumns
  const smVal = !image?.url ? fullWidthColumns : halfWidthColumns
  const ctaTextTrimmed = link?.copy ? link?.copy.trim() : undefined

  const theme = useContext(ThemeContext)
  const isSainsburys = theme?.channel === 'sainsburys'
  const imageWidth = isSainsburys ? 720 : 600
  const bgColorHex = colorToLunaToken(backgroundColor, theme)
  // use loop to do this for each of the breakpoints in utils
  const breakpoints = theme.size.breakpoint['max-width']
  const roundelBackground = roundel?.type === 'new' ? theme.color.content.new : theme.color.content.sale

  const imageVariables = useMemo(() => {
    // ratios used for isolated C001s
    const imageRatio = imageStyle === 'short' || imageStyle === 'normal' ? '2:1' : '3:2'
    // the space available in grids isn't wide enough to accomodate the above ratios, below logic is used instead. Based on the assumption only one variant of C001 is used in grids
    const ratioInGrid = isBrowse ? '5:4' : '3:2'
    const c001RatioMapping = {
      '2xs': !showFullWidth ? '4:5' : '3:2',
      xs: !showFullWidth ? '4:5' : '3:2',
      sm: imageRatio,
      md: imageRatio,
      lg: tabletImagePositionTop ? ratioInGrid : imageRatio,
      xl: tabletImagePositionTop ? ratioInGrid : imageRatio,
      '2xl': tabletImagePositionTop ? ratioInGrid : imageRatio
    }

    const additionalMedia = createPOIparam(imageRatio, image?.url, breakpoints, {
      ratioMap: c001RatioMapping,
      shouldSplit: (breakpointNumberWidth: number): boolean =>
        breakpointNumberWidth < 540 ? !showFullWidth : !tabletImagePositionTop
    })
    return { imageRatio, ratioInGrid, additionalMedia }
  }, [imageStyle, tabletImagePositionTop, mobileImagePosition, theme])

  const headingElement = (
    <StyledHeading
      className={isSainsburys ? 'ln-u-display-2' : undefined} // this is to mitigate the large font sizes on Luna, should be reverted after they've addressed
      as={isSecondaryHeading ? 'h2' : 'h1'}
      data-test='c001-heading'
      isOffer={roundel?.type === 'offer'}
    >
      {heading}
    </StyledHeading>
  )

  return (
    <Container
      {...getGOLDataAttributes('Load')}
      backgroundColor={backgroundColor}
      flipOrientation={flipOrientation}
      mobileImagePosition={mobileImagePosition}
      tabletImagePositionTop={tabletImagePositionTop}
      gutterValue={0}
      imageRatio={imageStyle}
      data-test='c001-container'
      forwardedAs={link?.url ? 'a' : undefined}
      href={link?.url ? agnostifyUrl(link?.url) : undefined}
      target={link?.url ? link?.target : undefined}
      onClick={(evt) => !!link?.url && ctaClick(evt)}
      title={link?.title}
    >
      <StyledContentWrapper
        xs={xsVal}
        sm={smVal}
        hasLogo={!!logo?.url}
        hasImage={!!image?.url}
        tabletImagePositionTop={tabletImagePositionTop}
        data-test='c001-content-wrapper'
        isCitrus={citrus}
      >
        <TextWrapper
          textAlignment={textAlignment}
          textColor={textColor}
          tabletImagePositionTop={tabletImagePositionTop}
          mobileImagePosition={mobileImagePosition}
          data-test='c001-text-wrapper'
        >
          {logo?.url && (
            <StyledLogoWrapper
              data-test='c001-logo'
              logo={`${logo?.url}?maxW=430`}
              alt={logo?.altText}
              textAlignment={textAlignment}
            />
          )}
          {headingElement}
          {subheading && <p data-test='c001-subheading'>{subheading}</p>}
          {link?.url && ctaTextTrimmed && (
            <StyledCTA
              data-test={`c001-cta-${link?.style}`}
              linkColor={linkColor}
              styleCTA={link?.style}
              forwardedAs='span'
              kind={link?.style === 'button' ? 'primary' : 'link'}
              role={link?.style === 'button' ? 'button' : 'link'}
            >
              {link?.copy}
            </StyledCTA>
          )}
        </TextWrapper>
      </StyledContentWrapper>
      {image?.url && (
        <StyledImageWrapper
          xs={xsVal}
          sm={halfWidthColumns}
          tabletImagePositionTop={tabletImagePositionTop}
          mobileImagePosition={!!showFullWidth}
          data-test='c001-image-wrapper'
          imageRatio={imageVariables.imageRatio}
          ratioInGrid={imageVariables.ratioInGrid}
        >
          <Image
            imageFormat={imageFormat}
            alt={image?.altText}
            src={image?.url}
            templates={[
              `w=${imageWidth}`,
              createRGBparam(bgColorHex),
              createPOIparam(tabletImagePositionTop ? imageVariables.ratioInGrid : imageVariables.imageRatio)
            ]}
            data-test='c001-image'
            aria-hidden='true'
            additionalMedia={imageVariables.additionalMedia}
          />
          {roundel && Object.keys(roundel).length > 0 && (
            <Roundel component='c001' roundel={roundel} backgroundColor={roundelBackground} />
          )}
        </StyledImageWrapper>
      )}
    </Container>
  )
}

export default memo(C001)
